import { useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'

import * as api from 'api/api'
import { ProjectInput, ProjectRole, User } from 'api/types'
import { useAuth } from 'auth/AuthContext'
import { Icon } from 'components/Icon'
import { ProjectForm } from 'components/ProjectForm'
import { UserFormModal } from 'components/UserFormModal'
import { Button, Card, ErrorState, IconButton, SelectField, Spinner, TextField } from 'components/ui'
import { formatDate, monthTitle } from 'lib/format'
import { useAction, useAsync } from 'lib/hooks'
import { t } from 'lib/i18n'

const stepLabels = () => [
  t('بيانات المشروع', 'Project details'),
  t('مناطق العمل', 'Work areas'),
  t('فريق العمل', 'Team'),
  t('المراجعة والإنشاء', 'Review and create'),
]

interface SiteDraft {
  key: string
  name: string
  scope: string
  engineerId: string
  shift: string
}

const newSiteDraft = (): SiteDraft => ({ engineerId: '', key: Math.random().toString(36).slice(2), name: '', scope: '', shift: '' })

const teamRoles = (): { role: ProjectRole; title: string; hint: string }[] => [
  {
    hint: t('يعتمد التقارير اليومية أو يرسلها للمراجعة', 'Approves daily reports or sends them for review'),
    role: 'PROJECT_MANAGER',
    title: t('مدير المشروع', 'Project Manager'),
  },
  {
    hint: t('يعبّئ التقارير اليومية ويرفق الملفات ويرسلها للاعتماد', 'Fills in daily reports, attaches files, and submits them for approval'),
    role: 'SITE_ENGINEER',
    title: t('مهندسو الموقع', 'Site engineers'),
  },
  {
    hint: t('يطّلع على التقارير المعتمدة فقط ويضيف ملاحظات الإشراف', 'Sees approved reports only and adds supervision notes'),
    role: 'SUPERVISOR',
    title: t('الإشراف', 'Supervisor'),
  },
]

type Team = Record<string, ProjectRole>

const Stepper = ({ step, onSelect }: { step: number; onSelect: (step: number) => void }) => (
  <ol className="stepper" aria-label={t('خطوات إنشاء المشروع', 'Project creation steps')}>
    {stepLabels().map((label, index) => (
      <li key={label} className={index === step ? 'is-current' : index < step ? 'is-done' : undefined}>
        <button type="button" disabled={index >= step} aria-current={index === step ? 'step' : undefined} onClick={() => onSelect(index)}>
          <span className="stepper__index">{index < step ? <Icon name="check" size={14} /> : index + 1}</span>
          <span>{label}</span>
        </button>
      </li>
    ))}
  </ol>
)

const SitesStep = ({ sites, users, onChange }: { sites: SiteDraft[]; users: User[]; onChange: (sites: SiteDraft[]) => void }) => {
  const update = (key: string, patch: Partial<SiteDraft>) =>
    onChange(sites.map((site) => (site.key === key ? { ...site, ...patch } : site)))
  const remove = (key: string) => onChange(sites.filter((site) => site.key !== key))

  return (
    <>
      <p className="muted">{t('أضف مناطق العمل التي يُسند لها مهندسون. يمكن إضافة المزيد لاحقاً من صفحة المشروع.', 'Add work areas and assign engineers to them. You can add more later from the project page.')}</p>
      <div className="stack">
        {sites.map((site, index) => (
          <Card
            key={site.key}
            title={t(`منطقة العمل ${index + 1}`, `Work area ${index + 1}`)}
            actions={
              <IconButton icon="trash" label={t(`حذف منطقة العمل ${index + 1}`, `Delete work area ${index + 1}`)} onClick={() => remove(site.key)} />
            }
          >
            <div className="form-grid">
              <TextField
                label={t('اسم المنطقة', 'Area name')}
                value={site.name}
                onChange={(event) => update(site.key, { name: event.target.value })}
                placeholder={t('مثال: المبنى الرئيسي', 'Example: Main building')}
              />
              <TextField
                label={t('النطاق / المساحة', 'Scope / area')}
                value={site.scope}
                onChange={(event) => update(site.key, { scope: event.target.value })}
                placeholder={t('مثال: من الأرضي إلى الطابق الثاني', 'Example: Ground floor to the second floor')}
              />
              <SelectField
                label={t('مهندس الموقع', 'Site Engineer')}
                value={site.engineerId}
                onChange={(event) => update(site.key, { engineerId: event.target.value })}
              >
                <option value="">{t('— غير مُسند —', '— Unassigned —')}</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </SelectField>
              <TextField
                label={t('الوردية', 'Shift')}
                value={site.shift}
                onChange={(event) => update(site.key, { shift: event.target.value })}
                placeholder={t('مثال: صباحية · 07:00–17:00', 'Example: Morning · 07:00–17:00')}
              />
            </div>
          </Card>
        ))}
      </div>
      <Button variant="ghost" icon="plus" onClick={() => onChange([...sites, newSiteDraft()])}>
        {t('إضافة منطقة عمل أخرى', 'Add another work area')}
      </Button>
      <div className="alert alert--info" role="status">
        <Icon name="file" />
        <span>{t('يمكنك إضافة مناطق عمل أو إيقافها أو إغلاقها من المشروع في أي وقت.', 'You can add work areas, put them on hold, or close them from the project at any time.')}</span>
      </div>
    </>
  )
}

const TeamStep = ({
  users,
  team,
  onChange,
  onUserCreated,
}: {
  users: User[]
  team: Team
  onChange: (team: Team) => void
  onUserCreated: (user: User) => void
}) => {
  const [creatingFor, setCreatingFor] = useState<ProjectRole | null>(null)
  const available = users.filter((user) => user.isActive && !user.isAdmin && !team[user.id])

  const assign = (userId: string, role: ProjectRole) => onChange({ ...team, [userId]: role })
  const unassign = (userId: string) => onChange(Object.fromEntries(Object.entries(team).filter(([id]) => id !== userId)))

  return (
    <>
      <p className="muted">
        {t('مدير النظام يملك صلاحية الوصول لكل المشاريع ولا يحتاج إلى إضافة. يمكن تعديل الفريق لاحقاً من إعدادات المشروع.', 'The admin can access every project and does not need to be added. You can change the team later in project settings.')}
      </p>
      <div className="team-grid">
        {teamRoles().map(({ role, title, hint }) => {
          const members = users.filter((user) => team[user.id] === role)
          return (
            <Card key={role} title={title} className="team-card">
              <p className="muted small">{hint}</p>
              {members.length ? (
                <ul className="team-list">
                  {members.map((user) => (
                    <li key={user.id}>
                      <div>
                        <strong>{user.name}</strong>
                        <span className="muted small ltr-cell" dir="ltr">
                          {user.email}
                        </span>
                      </div>
                      <IconButton icon="close" label={t(`إزالة ${user.name}`, `Remove ${user.name}`)} onClick={() => unassign(user.id)} />
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="team-empty">{t('لم يتم التحديد', 'None selected')}</p>
              )}
              <SelectField
                label={t(`إضافة إلى ${title}`, `Add to ${title}`)}
                value=""
                disabled={!available.length}
                onChange={(event) => event.target.value && assign(event.target.value, role)}
              >
                <option value="">{available.length ? t('اختر مستخدماً…', 'Choose a user…') : t('لا يوجد مستخدمون متاحون', 'No users available')}</option>
                {available.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name} — {user.email}
                  </option>
                ))}
              </SelectField>
              <Button size="sm" variant="ghost" icon="plus" onClick={() => setCreatingFor(role)}>
                {t('مستخدم جديد', 'New user')}
              </Button>
            </Card>
          )
        })}
      </div>

      {creatingFor ? (
        <UserFormModal
          title={`${t('مستخدم جديد', 'New user')} — ${teamRoles().find((entry) => entry.role === creatingFor)?.title ?? ''}`}
          onClose={() => setCreatingFor(null)}
          onCreated={(user) => {
            onUserCreated(user)
            assign(user.id, creatingFor)
            setCreatingFor(null)
          }}
        />
      ) : null}
    </>
  )
}

const DetailsSummary = ({ details }: { details: ProjectInput }) => {
  const rows: [string, string | null | undefined][] = [
    [t('اسم المشروع', 'Project name'), details.name],
    [t('رقم العطاء', 'Tender number'), details.tenderNumber],
    [t('الموقع', 'Location'), details.location],
    [t('صاحب العمل', 'Client'), details.client],
    [t('المقاول', 'Contractor'), details.contractor],
    [t('الإشراف', 'Supervisor'), details.consultant],
    [t('المدة', 'Duration'), `${formatDate(details.startDate)} – ${formatDate(details.endDate)}`],
  ]
  return (
    <dl className="summary-list">
      {rows.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value || '—'}</dd>
        </div>
      ))}
    </dl>
  )
}

const CalendarSummary = ({ details }: { details: ProjectInput }) => {
  const { data, error, loading, reload } = useAsync(
    () => api.fetchCalendarPreview(details.startDate, details.endDate),
    [details.startDate, details.endDate]
  )
  if (loading && !data) return <Spinner />
  if (error || !data) return <ErrorState message={error ?? t('تعذر حساب التقويم', 'Could not calculate the calendar')} onRetry={reload} />
  return (
    <div className="stack">
      <div className="stats stats--static stats--wide">
        <div className="stat">
          <span className="stat__value">{data.days}</span>
          <span className="stat__label">{t('تقرير يومي', 'Daily reports')}</span>
        </div>
        <div className="stat">
          <span className="stat__value">{data.months.length}</span>
          <span className="stat__label">{t('شهر', 'Months')}</span>
        </div>
        <div className="stat">
          <span className="stat__value code-link">{data.firstCode}</span>
          <span className="stat__label">{t('أول تقرير', 'First report')}</span>
        </div>
        <div className="stat">
          <span className="stat__value code-link">{data.lastCode}</span>
          <span className="stat__label">{t('آخر تقرير', 'Last report')}</span>
        </div>
      </div>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th scope="col">{t('الشهر', 'Month')}</th>
              <th scope="col">{t('عدد الأيام', 'Days')}</th>
              <th scope="col">{t('من', 'From')}</th>
              <th scope="col">{t('إلى', 'To')}</th>
            </tr>
          </thead>
          <tbody>
            {data.months.map((month) => (
              <tr key={month.month}>
                <td>{monthTitle(month.month)}</td>
                <td>{month.days}</td>
                <td className="code-link">{month.firstCode}</td>
                <td className="code-link">{month.lastCode}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="field__hint">{t('يُنشأ تقرير لكل يوم تقويمي بما فيها أيام الجمعة، ويبدأ الترقيم من 001 في كل شهر.', 'A report is created for every calendar day, including Fridays, and numbering starts at 001 each month.')}</p>
    </div>
  )
}

export const NewProjectPage = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [step, setStepState] = useState(0)
  const [details, setDetails] = useState<ProjectInput | null>(null)
  const [sites, setSites] = useState<SiteDraft[]>([])
  const [team, setTeam] = useState<Team>({})
  const { data: users, error, loading, reload, setData: setUsers } = useAsync(
    () => (user?.isAdmin ? api.fetchUsers() : Promise.resolve([])),
    [user?.isAdmin]
  )
  const { busy, run } = useAction()

  const setStep = (next: number) => {
    setStepState(next)
    window.scrollTo(0, 0)
  }

  if (!user?.isAdmin) return <Navigate to="/" replace />

  const roleCount = (role: ProjectRole) => Object.values(team).filter((entry) => entry === role).length

  const create = async () => {
    if (!details) return
    const members = Object.entries(team).map(([userId, role]) => ({ role, userId }))
    const project = await run('create', () => api.createProject(details, members), t('تم إنشاء المشروع', 'Project created'))
    if (!project) return
    const validSites = sites.filter((site) => site.name.trim())
    try {
      await Promise.all(
        validSites.map((site) =>
          api.createSite(project.id, {
            engineerId: site.engineerId || null,
            name: site.name.trim(),
            scope: site.scope.trim() || null,
            shift: site.shift.trim() || null,
            status: 'ACTIVE',
          })
        )
      )
    } catch {
      // project itself was created successfully; sites can still be added from the project's "مناطق العمل" tab
    }
    navigate(`/projects/${project.id}`)
  }

  return (
    <div className="page">
      <div className="page__head">
        <h1>{t('مشروع جديد', 'New project')}</h1>
      </div>
      <Stepper step={step} onSelect={setStep} />

      {step === 0 ? (
        <Card title={t('بيانات المشروع', 'Project details')}>
          <ProjectForm
            initial={details ?? undefined}
            submitLabel={t('التالي: مناطق العمل', 'Next: Work areas')}
            submitIcon="chevronLeft"
            note={t('تظهر هذه البيانات في ترويسة كل تقرير يومي وفي ملفات Excel وPDF.', 'These details appear in the header of every daily report and in the Excel and PDF files.')}
            onCancel={() => navigate('/')}
            onSubmit={(input) => {
              setDetails(input)
              setStep(1)
            }}
          />
        </Card>
      ) : null}

      {step === 1 ? (
        <>
          <SitesStep sites={sites} users={users ?? []} onChange={setSites} />
          <div className="form-actions">
            <Button icon="chevronRight" onClick={() => setStep(0)}>
              {t('السابق', 'Back')}
            </Button>
            <Button variant="primary" icon="chevronLeft" onClick={() => setStep(2)}>
              {t('التالي: فريق العمل', 'Next: Team')}
            </Button>
          </div>
        </>
      ) : null}

      {step === 2 ? (
        loading && !users ? (
          <Spinner />
        ) : error ? (
          <ErrorState message={error} onRetry={reload} />
        ) : (
          <>
            <TeamStep
              users={users ?? []}
              team={team}
              onChange={setTeam}
              onUserCreated={(created) => setUsers((current) => [...(current ?? []), { ...created, memberships: [] }])}
            />
            <div className="form-actions">
              <Button icon="chevronRight" onClick={() => setStep(1)}>
                {t('السابق', 'Back')}
              </Button>
              <Button variant="primary" icon="chevronLeft" onClick={() => setStep(3)}>
                {t('التالي: المراجعة', 'Next: Review')}
              </Button>
            </div>
          </>
        )
      ) : null}

      {step === 3 && details ? (
        <>
          <Card
            title={t('بيانات المشروع', 'Project details')}
            actions={
              <Button size="sm" variant="ghost" icon="edit" onClick={() => setStep(0)}>
                {t('تعديل', 'Edit')}
              </Button>
            }
          >
            <DetailsSummary details={details} />
          </Card>

          <Card title={t('تقويم التقارير', 'Report calendar')}>
            <CalendarSummary details={details} />
          </Card>

          <Card title={t('الكوادر الثابتة', 'Constant crew')}>
            <p>مدير المشروع · مهندس مدني · مهندس كهرباء · مهندس ميكانيكي · حاسب كميات · مراقب ابنية · حارس</p>
            <p className="muted small">
              {t(
                'تظهر هذه المسميات في كل تقرير يومي، ويُدخل العدد هناك. أي اختصاص آخر، مثل كاشف، يُضاف بعدده في التقرير نفسه.',
                'These roles are on every daily report. Enter the headcount there. Any other specialty, such as كاشف, is added that day with its headcount.',
              )}
            </p>
          </Card>

          <Card
            title={t('مناطق العمل', 'Work areas')}
            actions={
              <Button size="sm" variant="ghost" icon="edit" onClick={() => setStep(1)}>
                {t('تعديل', 'Edit')}
              </Button>
            }
          >
            {sites.filter((site) => site.name.trim()).length ? (
              <ul className="summary-list">
                {sites
                  .filter((site) => site.name.trim())
                  .map((site) => (
                    <li key={site.key}>
                      <strong>{site.name}</strong>
                      {site.engineerId ? ` — ${users?.find((user) => user.id === site.engineerId)?.name ?? ''}` : ''}
                    </li>
                  ))}
              </ul>
            ) : (
              <p className="muted small">{t('لم تُضف مناطق عمل بعد. يمكن إضافتها لاحقاً من صفحة المشروع.', 'No work areas added yet. You can add them later from the project page.')}</p>
            )}
          </Card>

          <Card
            title={t('فريق العمل', 'Team')}
            actions={
              <Button size="sm" variant="ghost" icon="edit" onClick={() => setStep(2)}>
                {t('تعديل', 'Edit')}
              </Button>
            }
          >
            <div className="stack">
            <dl className="summary-list">
              {teamRoles().map(({ role, title }) => {
                const names = (users ?? []).filter((entry) => team[entry.id] === role).map((entry) => entry.name)
                return (
                  <div key={role}>
                    <dt>{title}</dt>
                    <dd>{names.length ? names.join(t('، ', ', ')) : '—'}</dd>
                  </div>
                )
              })}
            </dl>
            {!roleCount('PROJECT_MANAGER') ? (
              <div className="alert alert--warning" role="status">
                <Icon name="alert" />
                <span>{t('لم يتم تحديد مدير مشروع، لذلك سيبقى اعتماد التقارير متاحاً لمدير النظام فقط.', 'No project manager is assigned, so only an admin can approve reports.')}</span>
              </div>
            ) : null}
            {!roleCount('SITE_ENGINEER') ? (
              <div className="alert alert--warning" role="status">
                <Icon name="alert" />
                <span>{t('لم يتم تحديد مهندس موقع، لذلك لن يتمكن أحد غير مدير النظام من تعبئة التقارير.', 'No site engineer is assigned, so only an admin can fill in reports.')}</span>
              </div>
            ) : null}
            </div>
          </Card>

          <div className="form-actions">
            <Button icon="chevronRight" onClick={() => setStep(2)}>
              {t('السابق', 'Back')}
            </Button>
            <Button variant="primary" icon="check" busy={busy === 'create'} onClick={create}>
              {t('إنشاء المشروع', 'Create project')}
            </Button>
          </div>
        </>
      ) : null}
    </div>
  )
}
